import React from 'react';
import styled from 'styled-components';
import { companiesRelationRequestsResponseType } from '../../../../../models/entities/company/companiesRelationRequest';
import { Formik, Form } from 'formik';
import { FormTextInput, FormDropDown } from '../../../../shared/inputs/form';
import DropDown from '../../../../shared/inputs/base/DropDown';
import Button from '../../../../shared/SmallComponents/Button';
import Loader from '../../../../shared/SmallComponents/Loader';
import { businessPartner } from '../../../../../models/entities/businessPartner/businessPartner';
import { companiesRelationType } from '../../../../../models/entities/company/companiesRelation';
import { useState } from 'react';
import { partiesToDropdownOptions } from '../../../businessPartners/components/BusinessPartnerForm/BusinessPartnerFormConnected';
import { Modal } from 'antd';
import FieldLabel from '../../../../shared/SmallComponents/FieldLabel';
import CopyToClipboard from '../../../../shared/CopyToClipboard';
import FormFieldLabel from '../../../../shared/Form/FormFieldLabel';

type Props = {
    localization: any;
    relationRequests: companiesRelationRequestsResponseType;
    businessPartners: Array<businessPartner>;
    businessPartnerCargoOwners: Array<businessPartner>;
    relationToken?: string;
    isGeneratingToken: boolean;
    approveCompaniesRelationRequest: (
        requestId: string,
        businessPartnerId: string,
        recievedBusinessPartnerId?: string,
        recievedRelationType?: companiesRelationType
    ) => Promise<void>;
    sendCompaniesRelationRequest: (
        token: string,
        relationType: companiesRelationType,
        recieverBusinessPartnerId?: string,
        recieverRelationType?: companiesRelationType
    ) => Promise<void>;
    denyCompaniesRelationRequest: (requestId: string) => Promise<void>;
    generateRelationToken: () => Promise<void>;
};

const RequestsSection = ({
    localization,
    relationRequests,
    businessPartners,
    businessPartnerCargoOwners,
    relationToken,
    isGeneratingToken,
    approveCompaniesRelationRequest,
    sendCompaniesRelationRequest,
    denyCompaniesRelationRequest,
    generateRelationToken
}: Props) => {
    const [isSubmit, setIsSubmit] = useState(false);
    const [isApprove, setIsApprove] = useState<false | string>(false);
    const [selectedBusinessPartners, setSelectedBusinessPartners] = useState<{ [key: string]: string }>({});

    const initialValues = {
        token: '',
        relationType: '',
        recieverBusinessPartnerId: null,
        recieverRelationType: null
    };

    const selectBusinessPartner = (requestId: string, businessPartnerId: string) => {
        const newSelectedBusinessPartners = { ...selectedBusinessPartners };
        newSelectedBusinessPartners[requestId] = businessPartnerId;
        setSelectedBusinessPartners(newSelectedBusinessPartners);
    };

    const onSubmit = async (values: any, { resetForm }: any) => {
        setIsSubmit(true);
        await sendCompaniesRelationRequest(
            values.token,
            values.relationType,
            values.recieverBusinessPartnerId || undefined,
            values.recieverRelationType || undefined
        );
        resetForm();
        setIsSubmit(false);
    };

    const validate = (values: any) => {
        let errors: any = {};

        if (!values.token) errors.token = localization.required;
        if (!values.relationType) errors.relationType = localization.required;

        return errors;
    };
    return (
        <Wrapper>
            <FormWrapper>
                <FormSection>
                    <SectionHeader>
                        <SectionTitle>{localization.generate_token || 'Generate Token'}</SectionTitle>
                        <SectionDescription>
                            {localization.generate_token_description || 'Generate a token to send a relation request to another company'}
                        </SectionDescription>
                    </SectionHeader>
                    <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                        {(formik) => {
                            return (
                                <Form>
                                    <RequiredFieldsSection>
                                        {!!relationToken && (
                                            <TokenDisplaySection>
                                                <GenerateTokenWrapper>
                                                    <CopyToClipboard value={relationToken} />
                                                    <TokenDisplay>{relationToken}</TokenDisplay>
                                                    {isGeneratingToken ? (
                                                        <Loader marginTop="0" showText={false} width="10px" inline />
                                                    ) : (
                                                        <GenerateButton
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                generateRelationToken();
                                                            }}
                                                        >
                                                            {localization.new_token || 'New token'}
                                                        </GenerateButton>
                                                    )}
                                                </GenerateTokenWrapper>
                                            </TokenDisplaySection>
                                        )}
                                        <FormContent>
                                            <TokenFieldWrapper>
                                                <FormFieldLabel
                                                    text={localization.token || 'Token'}
                                                    isMandatory={true}
                                                    tooltipText={localization.token_tooltip || 'The relation token to send with your request'}
                                                />
                                                <FormTextInput 
                                                    name="token" 
                                                    placeHolder={localization.token_placeholder || 'Enter or generate token'} 
                                                    style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }} 
                                                />
                                                {!relationToken && (
                                                    <GenerateTokenWrapper>
                                                        {isGeneratingToken ? (
                                                            <Loader marginTop="0" showText={false} width="10px" inline />
                                                        ) : (
                                                            <GenerateButton
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    generateRelationToken();
                                                                }}
                                                            >
                                                                {localization.generate_token || 'Generate token'}
                                                            </GenerateButton>
                                                        )}
                                                    </GenerateTokenWrapper>
                                                )}
                                            </TokenFieldWrapper>
                                            <FormFieldWrapper>
                                                <FormFieldLabel
                                                    text={localization.relationType || 'Relation Type'}
                                                    isMandatory={true}
                                                    tooltipText={localization.relationType_tooltip || 'Select the type of relation you want to establish.'}
                                                />
                                                <SearchableDropdownWrapper>
                                                    <SearchIcon className="material-icons">search</SearchIcon>
                                                    <FormDropDown
                                                        name="relationType"
                                                        placeHolder={localization.select_relationType || 'Type to search or select relation type'}
                                                        options={[
                                                            {
                                                                value: 'FORWARDER',
                                                                text: localization.forwarder
                                                            },
                                                            {
                                                                value: 'SUPPLIER',
                                                                text: localization.supplier
                                                            },
                                                            {
                                                                value: 'CARGO_OWNER',
                                                                text: localization.cargo_owner
                                                            }
                                                        ]}
                                                        showSearch
                                                        width="100%"
                                                    />
                                                </SearchableDropdownWrapper>
                                            </FormFieldWrapper>
                                            <ButtonWrapper>
                                                <Button buttonType="filled" height="36px" type="submit">
                                                    {isSubmit ? <Loader width="20px" marginTop="0px" showText={false} /> : <span>{localization.send}</span>}
                                                </Button>
                                            </ButtonWrapper>
                                        </FormContent>
                                    </RequiredFieldsSection>
                                    <OptionalFieldsSection>
                                        <OptionalSectionHeader>
                                            <OptionalSectionTitle>{localization.optional_fields || 'Optional Fields'}</OptionalSectionTitle>
                                            <OptionalSectionDescription>
                                                {localization.optional_fields_description || 'Specify receiver details if you want to send the request to a specific business partner'}
                                            </OptionalSectionDescription>
                                        </OptionalSectionHeader>
                                        <OptionalFormContent>
                                            <FormFieldWrapper>
                                                <FormFieldLabel
                                                    text={localization.recieverBusinessPartnerId || 'Receiver Business Partner'}
                                                    isMandatory={false}
                                                    tooltipText={localization.recieverBusinessPartnerId_tooltip || 'Select a specific business partner to receive this request (optional).'}
                                                />
                                                <SearchableDropdownWrapper>
                                                    <SearchIcon className="material-icons">search</SearchIcon>
                                                    <FormDropDown
                                                        name="recieverBusinessPartnerId"
                                                        placeHolder={localization.select_businessPartner || 'Type to search business partner'}
                                                        options={partiesToDropdownOptions([...businessPartners, ...businessPartnerCargoOwners])}
                                                        showSearch
                                                        allowClear
                                                        width="100%"
                                                    />
                                                </SearchableDropdownWrapper>
                                            </FormFieldWrapper>
                                            <FormFieldWrapper>
                                                <FormFieldLabel
                                                    text={localization.recieverRelationType || 'Receiver Relation Type'}
                                                    isMandatory={false}
                                                    tooltipText={localization.recieverRelationType_tooltip || 'Specify the relation type for the receiver (optional).'}
                                                />
                                                <SearchableDropdownWrapper>
                                                    <SearchIcon className="material-icons">search</SearchIcon>
                                                    <FormDropDown
                                                        name="recieverRelationType"
                                                        placeHolder={localization.select_receiverRelationType || 'Type to search or select relation type'}
                                                        options={[
                                                            {
                                                                value: 'FORWARDER',
                                                                text: localization.forwarder
                                                            },
                                                            {
                                                                value: 'SUPPLIER',
                                                                text: localization.supplier
                                                            },
                                                            {
                                                                value: 'CARGO_OWNER',
                                                                text: localization.cargo_owner
                                                            }
                                                        ]}
                                                        showSearch
                                                        allowClear
                                                        width="100%"
                                                    />
                                                </SearchableDropdownWrapper>
                                            </FormFieldWrapper>
                                        </OptionalFormContent>
                                    </OptionalFieldsSection>
                                </Form>
                            );
                        }}
                    </Formik>
                </FormSection>
            </FormWrapper>
            <RequestsWrapper>
                {!relationRequests.recieved.length && !relationRequests.sent.length ? (
                    <EmptyState>{localization.requests_empty_state}</EmptyState>
                ) : (
                    <>
                        <RequestsContainer>
                            {!!relationRequests.recieved.length && <RequestsHeader>{localization.recieved}</RequestsHeader>}
                            {relationRequests.recieved.map((req) => (
                                <ItemContainer key={req.id}>
                                    <span>
                                        <b>{req.senderCompanyName}</b>
                                        {' as '}
                                        {req.relationType}
                                    </span>
                                    {req.status === 'PENDING' && (
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <DropDown
                                                value={selectedBusinessPartners[req.id]}
                                                style={{ width: '200px', marginRight: 5 }}
                                                placeHolder={'Business Partner'}
                                                options={partiesToDropdownOptions(
                                                    req.relationType === 'CARGO_OWNER' ? businessPartnerCargoOwners : businessPartners
                                                )}
                                                onChange={(bpId: string) => {
                                                    selectBusinessPartner(req.id, bpId);
                                                }}
                                                showSearch
                                                allowClear
                                            />
                                            <Button
                                                margin="0 5px 0 0"
                                                buttonType="filled"
                                                disabled={!selectedBusinessPartners[req.id] && isApprove !== req.id}
                                                onClick={async () => {
                                                    const selectedBp = selectedBusinessPartners[req.id];
                                                    if (selectedBp) {
                                                        setIsApprove(req.id);
                                                        await approveCompaniesRelationRequest(
                                                            req.id,
                                                            selectedBp,
                                                            req.recieverBusinessPartnerId,
                                                            req.recieverRelationType
                                                        );
                                                        setIsApprove(false);
                                                    }
                                                }}
                                            >
                                                {isApprove === req.id ? (
                                                    <Loader width="20px" marginTop="0px" showText={false} />
                                                ) : (
                                                    <span>{localization.approve}</span>
                                                )}
                                            </Button>
                                            <span
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    Modal.confirm({
                                                        zIndex: 1100,
                                                        content: <div>{localization.are_you_sure}</div>,
                                                        async onOk() {
                                                            await denyCompaniesRelationRequest(req.id);
                                                        },
                                                        onCancel() {}
                                                    });
                                                }}
                                                className="material-icons"
                                                style={{ color: '#a2a2a2', fontSize: '12px', cursor: 'pointer' }}
                                            >
                                                close
                                            </span>
                                        </div>
                                    )}
                                    {req.status === 'DENIED' && <StatusText style={{ color: '#008ac9' }}>{req.status}</StatusText>}
                                </ItemContainer>
                            ))}
                        </RequestsContainer>
                        <RequestsContainer>
                            {!!relationRequests.sent.length && <RequestsHeader>{localization.sent}</RequestsHeader>}
                            {relationRequests.sent.map((req) => (
                                <ItemContainer key={req.id}>
                                    <span>
                                        {localization.waiting_for} <b>{req.recieverCompanyName}</b> {localization.to_approve}
                                    </span>
                                    <StatusText style={{ color: req.status === 'DENIED' ? '#f44336' : '#f0a333' }}>{req.status}</StatusText>
                                </ItemContainer>
                            ))}
                        </RequestsContainer>
                    </>
                )}
            </RequestsWrapper>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    padding: 25px;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const RequestsHeader = styled.div`
    color: #394372;
    font-size: 16px;
    font-weight: bold;
    margin: 10px;
`;

const FormWrapper = styled.div`
    width: 100%;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 24px;
    
    @media (max-width: 768px) {
        padding: 16px;
    }
`;

const FormSection = styled.div`
    width: 100%;
`;

const SectionHeader = styled.div`
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e4e4e4;
`;

const SectionTitle = styled.h3`
    color: #394372;
    font-size: 18px;
    font-weight: 600;
    margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
    color: #666;
    font-size: 14px;
    margin: 0;
    line-height: 1.5;
`;

const OptionalFieldsSection = styled.div`
    margin-top: 32px;
    padding-top: 24px;
    border-top: 2px dashed #e4e4e4;
`;

const OptionalSectionHeader = styled.div`
    margin-bottom: 16px;
`;

const OptionalSectionTitle = styled.h4`
    color: #666;
    font-size: 16px;
    font-weight: 500;
    margin: 0 0 6px 0;
`;

const OptionalSectionDescription = styled.p`
    color: #888;
    font-size: 13px;
    margin: 0;
    line-height: 1.4;
`;

const OptionalFormContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    align-items: flex-start;
    
    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
    }
`;

const RequestsWrapper = styled.div`
    width: 100%;
    flex: 1;
    align-items: center;
    justify-content: center;
    background-color: #fff;
    margin-top: 10px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    padding: 24px;
`;

const GenerateTokenWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    margin-top: 8px;
    flex-wrap: nowrap;
    overflow: visible;
    min-width: 0;
`;

const GenerateButton = styled.button`
    color: #1890ff;
    text-decoration: none;
    background-color: transparent;
    border: 1px solid #1890ff;
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 12px;
    outline: none;
    cursor: pointer;
    transition: all 0.3s;
    white-space: nowrap;
    flex-shrink: 0;
    flex-grow: 0;

    &:hover {
        color: #40a9ff;
        border-color: #40a9ff;
        background-color: #f0f8ff;
    }
`;

const TokenInputContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;

const TokenDisplay = styled.span`
    font-size: 12px;
    color: #666;
    font-family: monospace;
    white-space: nowrap;
    overflow: visible;
    flex: 0 1 auto;
    min-width: 0;
    max-width: 100%;
`;

const TokenFieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    
    /* Ensure input matches dropdown width */
    & input {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
    }
`;


const RequestInputWrapper = styled.div`
    width: 100%;
`;

const FormFieldWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: flex-start;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    
    /* Ensure dropdown aligns with input */
    & > div {
        width: 100%;
        max-width: 100%;
    }
`;

const RequiredFieldsSection = styled.div`
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    overflow: hidden;
`;

const TokenDisplaySection = styled.div`
    width: 100%;
    max-width: 100%;
    margin-bottom: 16px;
    padding-bottom: 16px;
    border-bottom: 1px solid #e4e4e4;
`;

const FormContent = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr auto;
    gap: 20px;
    align-items: start;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    
    /* Ensure all field wrappers have consistent structure */
    & > * {
        display: flex;
        flex-direction: column;
        min-width: 0;
        max-width: 100%;
    }
    
    /* Make sure Token and Relation Type inputs have equal width - force equal column widths */
    & > :first-child,
    & > :nth-child(2) {
        min-width: 0;
        max-width: 100%;
        width: 100%;
    }
    
    
    @media (max-width: 1024px) {
        grid-template-columns: 1fr;
        gap: 16px;
        
        & > :last-child {
            grid-column: 1;
            justify-self: stretch;
            margin-top: 0;
        }
    }
`;

const ButtonWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    min-width: 120px;
    padding-top: 28px; /* Match label height (5px margin-bottom from label + ~23px label height) */
    
    @media (max-width: 1024px) {
        padding-top: 0;
        margin-top: 0;
        width: 100%;
        
        & > button {
            width: 100%;
        }
    }
`;

const SearchableDropdownWrapper = styled.div`
    position: relative;
    width: 100%;
    max-width: 100%;
    min-width: 0;
    
    /* Adjust the dropdown input to make room for the icon */
    & > div {
        width: 100% !important;
        max-width: 100% !important;
        
        & .ant-select-selector {
            padding-left: 40px !important;
            padding-right: 30px !important;
            max-width: 100% !important;
            position: relative;
        }
        
        & .ant-select-selection-placeholder {
            left: 40px !important;
            right: 30px !important;
        }
        
        /* Selected item styling - when dropdown is closed */
        & .ant-select-selection-item {
            max-width: calc(100% - 0px) !important;
        }
        
        /* Search input base styling */
        & .ant-select-selection-search {
            position: absolute !important;
            left: 40px !important;
            right: 30px !important;
        }
`;

const SearchIcon = styled.span`
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #1890ff;
    font-size: 18px;
    pointer-events: none;
    z-index: 2;
    opacity: 0.7;
`;

const RequestsContainer = styled.div``;

const ItemContainer = styled.div`
    display: flex;
    align-items: center;
    height: 50px;
    background-color: #e5f4fa5e;
    justify-content: space-between;
    padding: 10px;
    border-bottom: 1px solid #e4e4e4;
`;

const StatusText = styled.b`
    font-size: 12px;
`;

const EmptyState = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 18px;
`;

export default RequestsSection;
